import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
class ThreeJs {
    base = THREE;
    scene = new this.base.Scene();
    camera = new this.base.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    renderer = new this.base.WebGLRenderer();
    loader = new GLTFLoader();
    controls;
    AMBIENT_COLOR = "#D4A25B";
    isAnimating = true;
    constructor(containerSelector) {
        this.setupRenderer(containerSelector);
        this.setupCamera();
        this.setupControls();
        this.setupLighting();
        this.startAnimation();
        this.observeElement(containerSelector);
    }
    async setupRenderer(containerSelector) {
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setClearColor(0x000000, 0);
        const container = document.querySelector(containerSelector);
        if (container) {
            container.appendChild(this.renderer.domElement);
        }
        else {
            console.error("Unable to find target container for 3JS renderer.");
            return;
        }
        window.addEventListener("resize", () => this.onWindowResize());
    }
    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 4;
    }
    setupCamera() {
        this.camera.position.z = 5;
    }
    setupLighting() {
        const ambientLight = new this.base.AmbientLight(0xD4A25B, 0.6);
        this.scene.add(ambientLight);
        const directionalLight = new this.base.DirectionalLight(0xD4A25B, 0.8);
        directionalLight.position.set(5, 5, 5);
        this.scene.add(directionalLight);
    }
    loadModel(modelPath) {
        this.loader.load(modelPath, (gltf) => {
            const model = gltf.scene;
            const ambientLight = new this.base.AmbientLight(this.AMBIENT_COLOR, 0.5);
            this.scene.add(model);
            this.scene.add(ambientLight);
            this.centerAndScaleModel(model);
        }, (_progress) => {
        }, (error) => {
            console.error("Error loading GLTF model:", error);
        });
    }
    centerAndScaleModel(model) {
        const box = new this.base.Box3().setFromObject(model);
        const center = box.getCenter(new this.base.Vector3());
        model.position.sub(center);
        const size = box.getSize(new this.base.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 6 / maxDim;
        model.scale.multiplyScalar(scale);
    }
    startAnimation() {
        const animate = () => {
            requestAnimationFrame(animate);
            if (!this.isAnimating)
                return;
            this.controls.update();
            this.renderer.render(this.scene, this.camera);
        };
        animate();
    }
    onWindowResize() {
        const width = window.innerWidth;
        const height = window.innerHeight;
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    }
    observeElement(targetSelector) {
        const targetElement = document.querySelector(targetSelector);
        if (!targetElement) {
            console.error(`Element with selector "${targetSelector}" not found.`);
            return;
        }
        const observerOptions = {
            root: null,
            rootMargin: "10px",
            threshold: 0.1,
        };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.isAnimating = true;
                    this.controls.autoRotate = true;
                }
                else {
                    this.isAnimating = false;
                    this.controls.autoRotate = false;
                }
            });
        }, observerOptions);
        observer.observe(targetElement);
    }
}
export default ThreeJs;
